/* eslint-disable react/jsx-props-no-spreading */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React from "react";
import AccountTreeIcon from "@material-ui/icons/AccountTree";
import {
  Typography,
  Switch,
  Paper,
  makeStyles,
  Theme,
  createStyles,
} from "@material-ui/core";
import { EditComponentProps } from "material-table";
import {
  UserGroupsApi,
  ConnectionsApi,
  FileCrawlingConfigsApi,
} from "../../../../helpers";
import {
  EditableTable,
  ColumnBaseHeaderStyle,
  ColumnBaseCellStyle,
  ConfirmDialog,
  PathCheckboxTree,
  getNodeIdAll,
} from "../../../../components";
import {
  UserGroupConfigDataRow,
  FileCrawlingConfigDataRow,
} from "../../../../types/config";
import { ConnectionEditComponent } from "./fields/ConnectionEditComponent";
import {
  PathCheckboxTreeData,
  PathCheckboxTreeDefs,
  PathCheckboxTreeNode,
} from "../../../../types";
import { flatten } from "../../../../components/tree/PathLabeledCheckbox";
import {
  crawlingModeDefs,
  FileCrawlingConfigHelper,
} from "./helpers/file-crawling-config-helper";
import { IsActiveRenderComponent } from "./fields/IsActiveRenderComponent";
import { CrawlingModeEditComponent } from "./fields/CrawlingModeEditComponent";
import { FiletypeAndScheduleRenderComponent } from "./fields/FiletypeAndScheduleRenderComponent";
import { FiletypeAndScheduleEditComponent } from "./fields/FiletypeAndScheduleEditComponent";

/** スタイル定義 */
const useStyles = makeStyles((_theme: Theme) =>
  createStyles({
    dialogPaper: {
      minWidth: "90vh",
      maxWidth: "90vh",
      minHeight: "80vh",
      maxHeight: "80vh",
    },
    typography: {
      fontSize: "0.875rem",
    },
    typographySmall: {
      fontSize: 11,
    },
  })
);

interface Props {
  parentData: UserGroupConfigDataRow;
  onChange?: () => void;
  onError?: (error: any) => void;
}

/** FILEクロール設定テーブル */
export function FileCrawlingConfigTable(props: Props) {
  const classes = useStyles();

  const { parentData, onChange, onError } = props;

  //------------------------------
  // Fileクロール設定
  const [data, setData] = React.useState<FileCrawlingConfigDataRow[]>([]);

  React.useEffect(() => {
    let cleanedUp = false;

    const fetchData = async () => {
      const userGroupId = parentData.id;
      if (userGroupId) {
        try {
          const res: any = await UserGroupsApi.retrieve(userGroupId);
          const rows = res.data.filecrawlingconfig_set.map((row: any) => {
            return FileCrawlingConfigHelper.toDataRow(row);
          });
          if (!cleanedUp) {
            setData(rows);
          }
        } catch (error: any) {
          if (onError) {
            if (!cleanedUp) {
              onError(error);
            }
          }
        }
      }
    };
    fetchData();

    return function cleanup() {
      cleanedUp = true;
    };
  }, []);

  //------------------------------
  // MaterialTable > Columns
  const columns: Array<any> = [
    {
      title: "有効",
      field: "isActive",
      headerStyle: ColumnBaseHeaderStyle,
      cellStyle: ColumnBaseCellStyle,
      initialEditValue: true,
      width: "5rem",
      customSort: (
        a: FileCrawlingConfigDataRow,
        b: FileCrawlingConfigDataRow
      ) => {
        let result = 0;
        if (a.isActive < b.isActive) {
          result = 1;
        } else if (a.isActive > b.isActive) {
          result = -1;
        }
        return result;
      },
      render: function renderIsActiveColumn(
        rowData: FileCrawlingConfigDataRow
      ) {
        // 表示画面ではスイッチ切替できる
        return (
          <IsActiveRenderComponent
            rowData={rowData}
            parentData={parentData}
            data={data}
            setData={setData}
          />
        );
      },
      editComponent: function editComponentIsActiveColumn(fieldProps: any) {
        // 編集画面ではスイッチ切替できない
        return (
          <Switch
            checked={fieldProps.value}
            color="primary"
            name="isActive"
            inputProps={{ "aria-label": "isActive checkbox" }}
            size="small"
            disabled
          />
        );
      },
    },
    {
      title: "接続先",
      field: "connection",
      width: "auto",
      headerStyle: ColumnBaseHeaderStyle,
      cellStyle: ColumnBaseCellStyle,
      initialEditValue: "",
      defaultSort: "asc",
      customSort: (
        a: FileCrawlingConfigDataRow,
        b: FileCrawlingConfigDataRow
      ) => {
        // a, b => -1 (0未満) の場合、... a, b, ... の順になる
        // a, b => 0 の場合、並び替えない
        // a, b => 1 (0より大きい)の場合、 ... b, a, ... の順になる
        const nameA =
          Object.keys(a.connection).length > 0 && "name" in a.connection
            ? a.connection.name
            : "";
        const nameB =
          Object.keys(b.connection).length > 0 && "name" in b.connection
            ? b.connection.name
            : "";

        // "test".localeCompare("223 NAS") => 1
        // "223 NAS".localeCompare("test") => -1
        // "test".localeCompare("test") => 0
        return nameA.localeCompare(nameB);
      },
      render: function renderConnectionColumn(
        rowData: FileCrawlingConfigDataRow
      ) {
        const { connection } = rowData;

        let component;
        if (
          "name" in connection &&
          "host" in connection &&
          "path" in connection
        ) {
          component = (
            <div>
              <div style={{ display: "flex", flexWrap: "wrap" }}>
                <Typography
                  component="span"
                  className={classes.typography}
                  style={{ marginRight: "0.5rem" }}
                >
                  {connection.name}
                </Typography>
                {connection.host && (
                  <Typography component="span" className={classes.typography}>
                    {`(${connection.host})`}
                  </Typography>
                )}
              </div>
              <div>
                {connection.path && (
                  <Typography
                    component="span"
                    className={classes.typographySmall}
                  >
                    {connection.path}
                  </Typography>
                )}
              </div>
            </div>
          );
        } else {
          component = (
            <Typography component="span" className={classes.typography}>
              未選択
            </Typography>
          );
        }
        return component;
      },
      editComponent: function editComponentConnectionColumn(
        fieldProps: EditComponentProps<FileCrawlingConfigDataRow>
      ) {
        return (
          <ConnectionEditComponent
            {...fieldProps}
            autoFocus
            onError={onError}
          />
        );
      },
    },
    {
      title: "クロール対象",
      field: "crawlingMode",
      width: "6rem",
      headerStyle: ColumnBaseHeaderStyle,
      cellStyle: ColumnBaseCellStyle,
      sorting: false,
      initialEditValue: "",
      render: function renderCrawlingModeColumn(
        rowData: FileCrawlingConfigDataRow
      ) {
        const { crawlingMode } = rowData;
        return (
          <div style={{ display: "flex", flexWrap: "wrap" }}>
            <Typography
              component="span"
              className={classes.typography}
              style={{ marginRight: "0.5rem" }}
            >
              {crawlingMode !== null && "name" in crawlingMode
                ? crawlingMode.name
                : "未選択"}
            </Typography>
          </div>
        );
      },
      editComponent: function editComponentCrawlingModeColumn(
        fieldProps: EditComponentProps<FileCrawlingConfigDataRow>
      ) {
        return (
          <CrawlingModeEditComponent
            {...fieldProps}
            autoFocus
            onError={onError}
          />
        );
      },
    },
    {
      title: "ファイル種類 / スケジュール",
      field: "filetypeAndSchedule",
      // width: "22rem",
      sorting: false,
      headerStyle: ColumnBaseHeaderStyle,
      cellStyle: ColumnBaseCellStyle,
      render: function renderFiletypeColumn(
        rowData: FileCrawlingConfigDataRow
      ) {
        return <FiletypeAndScheduleRenderComponent rowData={rowData} />;
      },
      editComponent: function editComponentFiletypeColumn(
        fieldProps: EditComponentProps<FileCrawlingConfigDataRow>
      ) {
        return <FiletypeAndScheduleEditComponent {...fieldProps} />;
      },
    },
  ];

  //------------------------------
  // Fileクロール対象ボックスツリー
  const [
    fileCrawlingTreeDialogOpen,
    setFileCrawlingTreeDialogOpen,
  ] = React.useState<boolean>(false);

  const [fileCrawlingTreeDefs, setFileCrawlingTreeDefs] = React.useState<
    PathCheckboxTreeDefs
  >({
    nodes: [],
  });

  const [fileCrawlingTreeData, setFileCrawlingTreeData] = React.useState<
    PathCheckboxTreeData
  >({
    checked: [],
    expanded: [],
  });

  const [
    selectedFileCrawlingConfigId,
    setSelectedFileCrawlingConfigId,
  ] = React.useState<number | null>(null);

  // [Editable] (作成、編集、削除)
  // [Handler]
  // add
  const getRowAddHandler = (
    state: FileCrawlingConfigDataRow[],
    setState: React.Dispatch<FileCrawlingConfigDataRow[]>
  ) => {
    return (newData: any) =>
      new Promise<void>((resolve, reject) => {
        resolve();
        // API Access
        if (parentData.id) {
          const newDbData = FileCrawlingConfigHelper.toDbRow(
            parentData.id,
            newData
          );
          FileCrawlingConfigsApi.create(newDbData)
            // 成功
            .then((res: any) => {
              resolve();
              const createdData = FileCrawlingConfigHelper.toDataRow(res.data);
              // Stateを更新する
              const innerData = [...state];
              innerData.push(createdData);
              setState(innerData);

              // 更新通知
              if (onChange) {
                onChange();
              }
              return true;
            })
            // 失敗
            .catch((error: any) => {
              reject(); // keep open row editable
              if (onError) {
                onError(error);
              }
              return false;
            });
        }
        return true;
      });
  };

  // update
  const getRowUpdateHandler = (
    state: FileCrawlingConfigDataRow[],
    setState: React.Dispatch<FileCrawlingConfigDataRow[]>
  ) => {
    return (
      newData: FileCrawlingConfigDataRow,
      oldData?: FileCrawlingConfigDataRow | undefined
    ) =>
      new Promise<void>((resolve, reject) => {
        if (oldData == null) {
          reject();
          return false;
        }

        // API Access
        if (parentData.id) {
          const newDbData = FileCrawlingConfigHelper.toDbRow(
            parentData.id,
            newData
          );
          FileCrawlingConfigsApi.update(oldData.id as number, newDbData)
            // 成功
            .then((res: any) => {
              resolve();
              const updatedData = FileCrawlingConfigHelper.toDataRow(res.data);
              // Stateを更新する
              const innerData = [...state];
              innerData[innerData.indexOf(oldData)] = updatedData;
              setState(innerData);
              return true;
            })
            // 失敗
            .catch((error: any) => {
              reject(); // keep open row editable
              if (onError) {
                onError(error);
              }
              return false;
            });
        }
        return true;
      });
  };

  // delete
  const getRowDeleteHandler = (
    state: FileCrawlingConfigDataRow[],
    setState: React.Dispatch<FileCrawlingConfigDataRow[]>
  ) => {
    return (oldData: any) =>
      new Promise<void>((resolve, reject) => {
        // API Access
        FileCrawlingConfigsApi.destroy(oldData.id as number)
          //------------------------------
          // 成功
          .then(() => {
            resolve();
            const innerData = [...state];
            innerData.splice(innerData.indexOf(oldData), 1);
            setState(innerData);

            // 更新通知
            if (onChange) {
              onChange();
            }
            return true;
          }) //------------------------------
          // 失敗
          .catch((error: any) => {
            reject(); // keep open row editable
            if (onError) {
              onError(error);
            }
            return false;
          });
      });
  };

  // Validation
  const validate = (values: FileCrawlingConfigDataRow) => {
    const errors: { [k: string]: any } = {};
    if (Object.keys(values.connection).length === 0) {
      errors.connection = "選択してください";
    }
    if (values.filetypeAndSchedule.schedule.length === 0) {
      errors.schedule = "1件以上指定してください";
    }
    return errors;
  };

  const getCheckboxTreeDefsChildrenNodes = (childrenNodes: any) => {
    if (!childrenNodes.length) {
      return [];
    }

    const nodes = childrenNodes.map((nodeData: any) => {
      return nodeData.children.length
        ? {
            nodeId: `node-${nodeData.id}`,
            label: nodeData.name,
            value: nodeData.path,
            children: getCheckboxTreeDefsChildrenNodes(nodeData.children),
          }
        : {
            nodeId: `node-${nodeData.id}`,
            label: nodeData.name,
            value: nodeData.path,
          };
    });
    return nodes;
  };

  const getCheckboxTreeDefsNodes = (rootNode: any): PathCheckboxTreeNode[] => {
    return [
      {
        nodeId: `node-${rootNode.id}`,
        label: rootNode.name,
        value: rootNode.path,
        children: getCheckboxTreeDefsChildrenNodes(rootNode.children),
      },
    ];
  };

  React.useEffect(() => {
    let cleanedUp = false;

    // 一覧取得
    const fetchData = async () => {
      if (selectedFileCrawlingConfigId) {
        try {
          // Fileクロール設定最新情報を取得する
          const resFileCrawlingConfig: any = await FileCrawlingConfigsApi.retrieve(
            selectedFileCrawlingConfigId
          );
          // 接続先情報を取得する
          const { connection } = resFileCrawlingConfig.data;
          const resGlob: any = await ConnectionsApi.glob(
            connection.host,
            connection.username,
            connection.password,
            connection.port,
            connection.path,
            true
          );
          // ツリー情報が取れていない場合、処理終了
          if (!Object.keys(resGlob.data).length) {
            if (!cleanedUp) {
              setFileCrawlingTreeDialogOpen(false);
            }
            return;
          }
          // Fileクロール対象ボックスツリーのノード作成
          const dataNodes = getCheckboxTreeDefsNodes(resGlob.data);
          if (!cleanedUp) {
            setFileCrawlingTreeDefs({
              nodes: dataNodes,
            });
          }
          // Fileクロール対象ボックスツリーのデータ作成
          const flattenNodes = flatten(dataNodes);
          const { ignore_pathlist } = resFileCrawlingConfig.data;
          const nowChecked = flattenNodes
            .filter((node: PathCheckboxTreeNode) => {
              return !ignore_pathlist.includes(node.value);
            })
            .map((node: PathCheckboxTreeNode) => {
              return node.nodeId;
            });
          if (!cleanedUp) {
            setFileCrawlingTreeData({
              checked: nowChecked,
              // 初期表示でツリーすべて開く場合はコメントアウト外す
              // expanded: getNodeIdAll(dataNodes),
              expanded: [],
            });
          }
        } catch (error: any) {
          if (onError) {
            if (!cleanedUp) {
              onError(error);
            }
          }
          if (!cleanedUp) {
            setFileCrawlingTreeDialogOpen(false);
          }
        }
      }
    };
    fetchData();

    return function cleanup() {
      cleanedUp = true;
    };
  }, [selectedFileCrawlingConfigId]);

  //--------------------
  // コンポーネント
  return (
    <div style={{ maxWidth: "100%" }}>
      <EditableTable<FileCrawlingConfigDataRow>
        title={(
          <Typography
            component="h6"
            style={{
              padding: "0.5rem",
              fontSize: "0.8rem",
              fontWeight: "bold",
            }}
          >
          Fileクロール設定
          </Typography>
        )}
        columns={columns}
        data={data}
        initialFormData={{
          connection: {},
          crawlingMode: crawlingModeDefs.find(v => v.value === "full"),
          filetypeAndSchedule: {
            filetype: {
              pdf: true,
              doc: true,
              xls: true,
              ppt: true,
              txt: true,
            },
            schedule: [FileCrawlingConfigHelper.getDefaultSchedule()],
          },
          isActive: true,
          ignorePathlist: [],
        }}
        editable={{
          onRowAdd: getRowAddHandler(data, setData),
          onRowUpdate: getRowUpdateHandler(data, setData),
          onRowDelete: getRowDeleteHandler(data, setData),
        }}
        validate={validate}
        actions={[
          {
            icon: function fileCrawlingTreeActionIcon() {
              return <AccountTreeIcon />;
            },
            tooltip: "クロール対象フォルダー選択",
            onClick: (
              _event: any,
              rowData: FileCrawlingConfigDataRow | FileCrawlingConfigDataRow[]
            ) => {
              if (Array.isArray(rowData)) return;
              // クリックされた行からFileクロール設定ID取得
              const fileCrawlingConfigId: number | undefined = rowData.id;
              if (typeof fileCrawlingConfigId !== "number") return;
              setSelectedFileCrawlingConfigId(fileCrawlingConfigId);
              // ダイアログを開く
              setFileCrawlingTreeDialogOpen(true);
            },
          },
        ]}
        components={{
          Container: function customContainer(componentProps: any) {
            return <Paper elevation={0}>{componentProps.children}</Paper>;
          },
        }}
        options={{
          showTitle: true,
        }}
      />
      <ConfirmDialog
        open={fileCrawlingTreeDialogOpen}
        title="Fileクロール対象選択"
        dialogClasses={{ paper: classes.dialogPaper }}
        okLabel="　保存　"
        onOk={async () => {
          if (selectedFileCrawlingConfigId) {
            try {
              // 保存処理
              const flattenNodes = flatten(fileCrawlingTreeDefs.nodes);
              const ignore_pathlist = flattenNodes
                .filter((node: PathCheckboxTreeNode) => {
                  return !fileCrawlingTreeData.checked.includes(node.nodeId);
                })
                .map((node: PathCheckboxTreeNode) => {
                  return node.value;
                });
              const res: any = await FileCrawlingConfigsApi.update_ignore_pathlist(
                selectedFileCrawlingConfigId,
                ignore_pathlist
              );
              const updatedData = FileCrawlingConfigHelper.toDataRow(res.data);
              // Stateを更新する
              const innerData = [...data];
              innerData[
                innerData.findIndex(
                  row => row.id === selectedFileCrawlingConfigId
                )
              ] = updatedData;
              setData(innerData);
            } catch (error: any) {
              if (onError) {
                onError(error);
              }
            } finally {
              setFileCrawlingTreeDialogOpen(false);
            }
          } else {
            setFileCrawlingTreeDialogOpen(false);
          }
        }}
        onCancel={() => {
          setFileCrawlingTreeDialogOpen(false);
        }}
      >
        <PathCheckboxTree
          defs={fileCrawlingTreeDefs}
          data={fileCrawlingTreeData}
          handleChange={(newData: PathCheckboxTreeData) => {
            setFileCrawlingTreeData(newData);
          }}
        />
      </ConfirmDialog>
    </div>
  );
}
